/**
 * Directory-audit rows grouped into ACTIONS, and named after the operation that describes
 * the action.
 *
 * Microsoft records one thing a person did as several directory-audit rows. v1.152.0
 * marked them with a badge ("1 of 2") keyed on the Entra `correlationId`. Two measured
 * problems with that, both found from a live case:
 *
 * 🔴 1. CORRELATION ID IS NOT SUFFICIENT. A B2B guest invitation arrives as THREE rows at
 *    the same second, under TWO correlation ids: `Invite external user` under one (from
 *    the caller's address), `Add user` + `Update PasswordProfile` under another (from
 *    Microsoft's B2B Admin Worker address). So the badge said "1 of 2" for an action that
 *    is three rows, presenting the third as unrelated activity. Measured across the whole
 *    case: **7 of 23 same-second actions span more than one correlation id** — the invite
 *    is not a special case.
 *
 * 🔴 2. A SAME-SECOND KEY IS NOT SUFFICIENT EITHER. An admin password reset is three rows
 *    at :37, :46 and :47 — one action, ten seconds wide. Hence the window.
 *
 * So the key is (target object, contiguous within a window). Measured on
 * IR-20260818-Controller-001: 45 rows -> 23 actions at 0s, **13 at 15s or 30s**, 11 at
 * 60s, 10 at 120s.
 *
 * DEFAULT 30s, NOT 60s. 60s saves two rows by merging `Admin deleted security info` into
 * the adjacent password reset — defensible as one remediation, but it blends two separate
 * admin decisions, and the risk being managed here is merging actions that are genuinely
 * distinct. Conservative wins.
 *
 * NAMING. The row is named after the operation that describes what happened — `Invite
 * external user`, never `Update PasswordProfile`. The rest are Microsoft's plumbing for it
 * and become constituents on expand. A standalone `Add user` with no invite still gets its
 * own row and its own name, which matters: account creation was deliberately un-filtered
 * in v1.152.0 because creating an identity is a common way an attacker keeps access.
 *
 * Nothing is discarded — every row rides on `_actionIds`, so dismissal and any per-row
 * state cover the whole action, and the Directory Audit sub-tab is untouched.
 *
 * 🔴 3. A TARGET IS NOT ALWAYS THE WHOLE KEY. Two device registrations three seconds
 *    apart share the same target (the USER being given the device) and merged into one
 *    four-row action that could name only one of the two machines. Two device objects
 *    created is two facts, and the finding that matters is a COUNT ("six attacker devices
 *    were joined"), so a merge that renders five rows answers the question wrongly. Device
 *    rows therefore carry a discriminator — see `deviceActionDiscriminator`, which returns
 *    '' for everything else, leaving all other grouping exactly as it was.
 */
import { deviceActionDiscriminator } from './deviceRegistration.js';

export const DEFAULT_ACTION_WINDOW_SECONDS = 30;

/**
 * Operations that DESCRIBE an action, most descriptive first. Anything not listed sorts
 * last and can still be a principal when it is the only row in its action — so an
 * unlisted future operation degrades to "named after itself", never to a blank row.
 */
const PRINCIPAL_ORDER = [
  'invite external user',
  'redeem external user invite',
  'delete user',
  'disable account',
  'add member to role',
  'add member to role in pim requested (permanent)',
  'reset user password',
  'reset password (by admin)',
  'change user password',
  'change password (self-service)',
  'update stsrefreshtokenvalidfrom timestamp',
  'admin deleted security info',
  'user deleted security info',
  'user registered security info',
  'user registered all required security info',
  'user started security info registration',
  'add user',
  'add registered owner to device',
  'add registered users to device',
  'add device',
  'update user',
];

/**
 * Operations that are ALWAYS a byproduct of something else and must never name an action.
 *
 * 🔴 These rank BELOW an unrecognised operation, deliberately. Listing them at the end of
 * PRINCIPAL_ORDER is not enough: an unknown operation scores `length`, which would beat
 * anything listed, so a future operation paired with `Update PasswordProfile` would have
 * been named after the password profile — the exact unreadable row this design exists to
 * prevent. An unknown operation is at least *specific*; this one never is.
 */
const ALWAYS_BYPRODUCT = new Set(['update passwordprofile']);

const rankOf = (op) => {
  const key = String(op || '').trim().toLowerCase();
  if (ALWAYS_BYPRODUCT.has(key)) return PRINCIPAL_ORDER.length + 1;   // below unknown
  const i = PRINCIPAL_ORDER.indexOf(key);
  return i === -1 ? PRINCIPAL_ORDER.length : i;                        // unknown
};

/** The object the action was performed ON. Rows sharing a target at the same moment are
 *  the same action even when Microsoft split the correlation id. */
export function actionTarget(event) {
  const raw = event?.RawLog;
  const list = (raw && typeof raw === 'object' && raw.targetResources) || [];
  if (Array.isArray(list)) {
    for (const t of list) {
      const v = t?.userPrincipalName || t?.displayName || t?.id;
      if (v) return String(v).toLowerCase();
    }
  }
  return '';
}

function correlationId(event) {
  const raw = event?.RawLog;
  return (raw && typeof raw === 'object' && raw.correlationId) ? String(raw.correlationId) : '';
}

/**
 * Group directory-audit events into actions.
 * @returns array of action rows (a superset of the principal event) and untouched singles.
 */
export function buildDirectoryActions(events, opts = {}) {
  const list = (Array.isArray(events) ? events : []).filter((e) => e && typeof e === 'object');
  const windowMs = (opts.windowSeconds ?? DEFAULT_ACTION_WINDOW_SECONDS) * 1000;

  // Stream by target; rows with no target fall back to correlationId, which is still
  // better than treating each as its own action.
  const streams = new Map();
  for (const e of list) {
    const t = actionTarget(e);
    const disc = deviceActionDiscriminator(e);
    const base = t ? `t:${t}` : `c:${correlationId(e) || e.EventId}`;
    const k = disc ? `${base}|d:${disc}` : base;
    const s = streams.get(k);
    if (s) s.push(e);
    else streams.set(k, [e]);
  }

  const out = [];
  for (const members of streams.values()) {
    const sorted = [...members].sort((a, b) =>
      String(a.EventTimeUTC || '').localeCompare(String(b.EventTimeUTC || '')));
    let run = [sorted[0]];
    const flush = () => out.push(makeAction(run));
    for (let i = 1; i < sorted.length; i += 1) {
      const prev = Date.parse(sorted[i - 1].EventTimeUTC);
      const cur = Date.parse(sorted[i].EventTimeUTC);
      // An unparseable timestamp breaks the run rather than silently extending it.
      const contiguous = Number.isFinite(prev) && Number.isFinite(cur) && (cur - prev) <= windowMs;
      if (contiguous) run.push(sorted[i]);
      else { flush(); run = [sorted[i]]; }
    }
    flush();
  }
  return out;
}

function makeAction(run) {
  // The principal is the most descriptive operation; ties break on time so the choice is
  // stable across renders.
  const ordered = [...run].sort((a, b) =>
    (rankOf(a.Operation) - rankOf(b.Operation))
    || String(a.EventTimeUTC || '').localeCompare(String(b.EventTimeUTC || '')));
  const principal = ordered[0];
  const byTime = [...run].sort((a, b) =>
    String(a.EventTimeUTC || '').localeCompare(String(b.EventTimeUTC || '')));
  const cids = [...new Set(run.map(correlationId).filter(Boolean))];

  if (run.length === 1) {
    return { ...principal, _action: false, _actionIds: [principal.EventId] };
  }
  return {
    ...principal,
    // The action is timestamped at its FIRST row, not at the principal's row: the story
    // is "when did this happen", and the principal can be a byproduct written a second
    // later than the row that started it.
    EventTimeUTC: byTime[0].EventTimeUTC,
    _action: true,
    _actionIds: byTime.map((e) => e.EventId),
    _actionRowCount: run.length,
    _actionPrincipalOp: principal.Operation,
    _actionCorrelationIds: cids,
    // The tell that correlationId alone would have missed this action.
    _actionSpansCorrelationIds: cids.length > 1,
    _actionTarget: actionTarget(principal),
    _actionRows: byTime.map((e) => ({
      eventId: e.EventId,
      timeUtc: e.EventTimeUTC,
      op: e.Operation,
      ip: e.ResolvedUserIP || e.IPAddress || '',
      correlationId: correlationId(e),
      isPrincipal: e.EventId === principal.EventId,
    })),
    // Byproduct operations with counts, for the one-line summary.
    _actionOtherOps: summariseOps(byTime.filter((e) => e.EventId !== principal.EventId)),
  };
}

function summariseOps(rows) {
  const counts = new Map();
  for (const r of rows) {
    const op = r.Operation || 'unknown';
    counts.set(op, (counts.get(op) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([op, count]) => ({ op, count }));
}

/** Roll only the directory-audit rows of a mixed stream into actions. */
export function buildDirectoryActionsInStream(stream, opts = {}) {
  const list = Array.isArray(stream) ? stream : [];
  const subject = list.filter((e) => e && e._eventType === 'DirectoryAudit');
  if (subject.length === 0) return list;
  const actions = buildDirectoryActions(subject, opts);
  const byFirst = new Map();
  const consumed = new Set();
  for (const a of actions) {
    const ids = a._actionIds || [];
    byFirst.set(ids[0], a);
    for (const id of ids.slice(1)) consumed.add(id);
  }
  const out = [];
  for (const e of list) {
    if (!e || e._eventType !== 'DirectoryAudit') { out.push(e); continue; }
    if (consumed.has(e.EventId)) continue;
    out.push(byFirst.get(e.EventId) || e);
  }
  return out;
}

/** Suffix naming the byproduct rows, e.g. " (+2: Add user, Update PasswordProfile)". */
export function actionByproductSuffix(action) {
  const others = action?._actionOtherOps || [];
  if (!others.length) return '';
  const shown = others.slice(0, 3)
    .map((o) => (o.count > 1 ? `${o.op} ×${o.count}` : o.op)).join(', ');
  const total = others.reduce((s, o) => s + o.count, 0);
  const more = others.length > 3 ? ', …' : '';
  return ` (+${total}: ${shown}${more})`;
}
